import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  makeStyles
} from "@material-ui/core";
import { Search, Clear, FilterList } from "@material-ui/icons";
import { useLazyQuery } from "@apollo/client";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { GET_CREDIT_NOTES } from "../../../../graphql/creditNotes/getCreditNotesQuery";
import { IAppState } from "../../../../store";
import { getLocalizedBookingSyntex, getLocalizedDateFormat } from "../../../../utils/localized.syntex";
import { StyledTableCell, StyledTableRow } from "../../../StyledTable";
import { DATE_TYPE, toCurrency } from "../../../utils";
import { ICreditNote } from "../../../../reducers/bookings/types";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  chip: {
    fontSize: "12px",
    height: "24px",
    borderRadius: "12px",
    fontWeight: 500
  },
  filterCard: {
    padding: "16px",
    marginBottom: "16px",
    backgroundColor: "#f5f5f5"
  },
  tabs: {
    marginBottom: "16px",
    backgroundColor: "#fff",
    borderRadius: "4px"
  },
  statusChip: {
    fontWeight: 600,
    fontSize: "11px"
  },
  balanceBox: {
    padding: "12px 16px",
    marginBottom: "16px",
    backgroundColor: "#e3f2fd",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    gap: "8px"
  }
}));

const CreditNotes: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const { t } = useTranslation();
  const user = useSelector((state: IAppState) => state.authReducer.user);
  const website = useSelector(
    (state: IAppState) => state.consumerWebsiteReducer.consumerWebsite
  );
  const { currency, locale } = website.organisation;
  const { country } = website.organisation.address;

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [sortBy, setSortBy] = useState<keyof ICreditNote>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [creditNotes, setCreditNotes] = useState<ICreditNote[]>([]);
  const [allCreditNotes, setAllCreditNotes] = useState<ICreditNote[]>([]);
  const [availableBalance, setAvailableBalance] = useState(0);
  const [tempStatuses, setTempStatuses] = useState<string[]>([]);
  
  useEffect(() =>{
    if(filterModalOpen){
      setTempStatuses(selectedStatuses);
    }
  }, [filterModalOpen, selectedStatuses]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchInput !== searchTerm) {
        setSearchTerm(searchInput);
        setPage(0);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  const [getCreditNotes, { loading, error }] = useLazyQuery(
    GET_CREDIT_NOTES,
    {
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        if (data?.consumerCreditNotes) {
          setAllCreditNotes(data.consumerCreditNotes);
        }
      }
    }
  );

  useEffect(() => {
    if (user) {
      getCreditNotes({
        variables: {
          businessCustomerId: user.businesscustomers?.id || undefined,
          customerId: user.customerId || undefined,
        },
      });
    }
  }, [user]);
  
  useEffect(() => {
    setAvailableBalance(
      allCreditNotes.reduce(
      (sum, creditNote) => sum + (creditNote.availableAmount || 0),
      0
      )
    );
  }, [allCreditNotes]);

  useEffect(() => {
    let filtered = [...allCreditNotes];

    if (selectedStatuses.length > 0) {
      filtered = filtered.filter((creditNote) => {
        if (selectedStatuses.includes("ACTIVE") && creditNote.availableAmount > 0) {
          return true;
        }
        if (selectedStatuses.includes("EXHAUSTED") && creditNote.availableAmount === 0) {
          return true;
        }
        return false;
      });
    }

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((creditNote) =>
        creditNote.creditNoteRef.toLowerCase().includes(searchLower) ||
        creditNote.booking?.referenceNumber?.toLowerCase().includes(searchLower)
      );
    }

    filtered.sort((a, b) => {
      const aValue = a[sortBy];
      const bValue = b[sortBy];
      if (!aValue) return 1;
      if (!bValue) return -1;
      
      const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      return sortOrder === "asc" ? comparison : -comparison;
    });

    const startIndex = page * rowsPerPage;
    const paginatedCreditNotes = filtered.slice(startIndex, startIndex + rowsPerPage);
    
    setCreditNotes(paginatedCreditNotes);
  }, [allCreditNotes, searchTerm, selectedStatuses, sortBy, sortOrder, page, rowsPerPage]);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
  }, []);

  const handleStatusFilterChange = useCallback((event: any) => {
    const value = event.target.value as string[];
    setTempStatuses(value);
  }, []);

  const handleApplyFilters = useCallback(() => {
   setSelectedStatuses(tempStatuses);
   setPage(0);
   setFilterModalOpen(false); 
  }, [tempStatuses])

  const handleResetFilters = useCallback(() => {
    setSelectedStatuses([]);
    setTempStatuses([]);
    setPage(0);
  }, []);

  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleRequestSort = useCallback((property: keyof ICreditNote) => {
    const isAsc = sortBy === property && sortOrder === "asc";
    setSortOrder(isAsc ? "desc" : "asc");
    setSortBy(property);
  }, [sortBy, sortOrder]);

  const handleClearFilters = useCallback(() => {
    setSearchInput("");
    setSearchTerm("");
    setSelectedStatuses([]);
    setTempStatuses([]);
    setPage(0);
  }, []);

  const getTotalCount = () => {
    let filtered = [...allCreditNotes];
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter((creditNote) => {
        if (selectedStatuses.includes("ACTIVE") && creditNote.availableAmount > 0) {
          return true;
        }
        if (selectedStatuses.includes("EXHAUSTED") && creditNote.availableAmount === 0) {
          return true;
        }
        return false;
      });
    }
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((creditNote) =>
        creditNote.creditNoteRef.toLowerCase().includes(searchLower) ||
        creditNote.booking?.referenceNumber?.toLowerCase().includes(searchLower)
      );
    }
    return filtered.length;
  };

  const totalCount = getTotalCount();

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">Error loading credit notes: {error.message}</Typography>
      </Box>
    );
  }

  return (
    <div style={{ padding: "24px" }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: "24px" }}>
        <Typography variant="h3" style={{ fontWeight: 600, fontSize: "2rem" }}>
          {t("root:creditNotes")}
        </Typography>

        <Box display="flex" alignItems="center" style={{ gap: "16px" }}>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setFilterModalOpen(true)}
            style={{
              height: "40px",
              textTransform: "none",
              borderColor: selectedStatuses.length > 0 ? "#1976d2" : "rgba(0, 0, 0, 0.23)",
              color: selectedStatuses.length > 0 ? "#1976d2" : "inherit"
            }}
          >
            Filters {selectedStatuses.length > 0 && `(${selectedStatuses.length})`}
          </Button>

          {selectedStatuses.length > 0 && (
            <Button 
              variant="outlined" 
              startIcon={<Clear />} 
              onClick={handleClearFilters}
              style={{ 
                whiteSpace: "nowrap",
                height: "40px",
                textTransform: "none",
                borderColor: "rgba(0, 0, 0, 0.23)"
              }}
            >
              Clear All
            </Button>
          )}
        </Box>
      </Box>

      <Box className={classes.balanceBox}>
        <Typography variant="h6" style={{ fontWeight: 600 }}>
          {t("root:availableBalance")}:
        </Typography>
        <Typography variant="h6" style={{ fontWeight: 600, color: "#1976d2" }}>
          {toCurrency(availableBalance, currency, locale)}
        </Typography>
      </Box>

      <Dialog
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" style={{ fontWeight: 600 }}>FILTERS</Typography>
            <Box>
              <Button
                color="primary"
                onClick={handleResetFilters}
                style={{ textTransform: "none", marginRight: 8 }}
              >
                RESET
              </Button>
              <IconButton size="small" onClick={() => setFilterModalOpen(false)}>
                <Clear />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  multiple
                  value={tempStatuses}
                  onChange={handleStatusFilterChange}
                  MenuProps={{
                    getContentAnchorEl: null
                  }}
                  input={<OutlinedInput label="Status" />}
                  renderValue={(selected) => 
                    (selected as string[]).length === 0 ? "All" : (selected as string[]).join(", ")
                  }
                >
                  <MenuItem value="ACTIVE">
                    <Checkbox checked={tempStatuses.includes("ACTIVE")} />
                    <ListItemText primary={t("root:active")} />
                  </MenuItem>
                  <MenuItem value="EXHAUSTED">
                    <Checkbox checked={tempStatuses.includes("EXHAUSTED")} />
                    <ListItemText primary={t("root:exhausted")} />
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
          onClick={handleApplyFilters} color="primary" variant="contained"
          disabled={tempStatuses.length === 0}
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      <Card className={classes.filterCard}>
        <Grid container spacing={2} alignItems="flex-start">
          {selectedStatuses.length > 0 && (
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: "8px" }}>
                <Typography variant="body2" style={{ fontWeight: 600, marginRight: 8 }}>
                  Active Filters:
                </Typography>
                {selectedStatuses.map((status) => (
                  <Chip
                    key={status}
                    label={status === "ACTIVE" ? t("root:active") : t("root:exhausted")}
                    onDelete={() => {
                      setSelectedStatuses(selectedStatuses.filter(s => s !== status));
                      setPage(0);
                    }}
                    color="primary"
                    size="small"
                    className={classes.chip}
                  />
                ))}
              </Box>
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              placeholder={`${t("root:searchCreditNoteId")} or ${t("root:bookingRef")}`}
              value={searchInput}
              onChange={handleSearchChange}
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: searchInput && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchInput("");
                        setSearchTerm("");
                      }}
                      edge="end"
                    >
                      <Clear />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
        </Grid>
      </Card>

      <Box style={{ position: "relative" }}>
        {loading && (
          <Box
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(255,255,255,0.7)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10
            }}
          >
            <CircularProgress />
          </Box>
        )}
        
        <TableContainer style={{ maxHeight: "600px" }}>
          <Table stickyHeader aria-label="credit notes table">
              <TableHead>
                <TableRow>
                <StyledTableCell>
                  <TableSortLabel
                    active={sortBy === "creditNoteRef"}
                    direction={sortBy === "creditNoteRef" ? sortOrder : "asc"}
                    onClick={() => handleRequestSort("creditNoteRef")}
                  >
                    {t("root:creditNoteId")}
                  </TableSortLabel>
                </StyledTableCell>
                  <StyledTableCell>{`${getLocalizedBookingSyntex(country)} ${t("root:ref")}`}</StyledTableCell>
                <StyledTableCell>
                  <TableSortLabel
                    active={sortBy === "createdAt"}
                    direction={sortBy === "createdAt" ? sortOrder : "asc"}
                    onClick={() => handleRequestSort("createdAt")}
                  >
                    {t("root:dateGenerated")}
                  </TableSortLabel>
                </StyledTableCell>
                <StyledTableCell>
                  <TableSortLabel
                    active={sortBy === "totalAmount"}
                    direction={sortBy === "totalAmount" ? sortOrder : "asc"}
                    onClick={() => handleRequestSort("totalAmount")}
                  >
                    {t("root:total")}
                  </TableSortLabel>
                </StyledTableCell>
                <StyledTableCell>
                  <TableSortLabel
                    active={sortBy === "refundedAmount"}
                    direction={sortBy === "refundedAmount" ? sortOrder : "asc"}
                    onClick={() => handleRequestSort("refundedAmount")}
                  >
                    {t("root:amountRefunded")}
                  </TableSortLabel>
                </StyledTableCell>
                <StyledTableCell>
                  <TableSortLabel
                    active={sortBy === "availableAmount"}
                    direction={sortBy === "availableAmount" ? sortOrder : "asc"}
                    onClick={() => handleRequestSort("availableAmount")}
                  >
                    {t("root:amountRemaining")}
                  </TableSortLabel>
                </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
              {creditNotes.length > 0 ? (
                creditNotes.map((creditNote, index) => (
                    <StyledTableRow
                    key={creditNote.id || index}
                    onClick={() => history.push(`/account/credit-notes/${creditNote.id}`)}
                    style={{ cursor: "pointer" }}
                    >
                      <StyledTableCell>{creditNote.creditNoteRef}</StyledTableCell>
                      <StyledTableCell>
                      {creditNote.booking?.referenceNumber || t("root:na")}
                      </StyledTableCell>
                      <StyledTableCell>
                      {getLocalizedDateFormat(
                        country,
                        creditNote.createdAt,
                        DATE_TYPE.EXPANDED,
                        creditNote.booking?.branch?.timeZone
                        )}
                      </StyledTableCell>
                    <StyledTableCell>
                      {toCurrency(creditNote.totalAmount, currency, locale)}
                    </StyledTableCell>
                      <StyledTableCell>
                        {toCurrency(creditNote.refundedAmount, currency, locale)}
                      </StyledTableCell>
                      <StyledTableCell>
                      <span
                        style={{
                          fontWeight: 600,
                          color: creditNote.availableAmount > 0 ? "#2e7d32" : "#757575"
                        }}
                      >
                        {toCurrency(creditNote.availableAmount, currency, locale)}
                      </span>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
                ) : (
                <TableRow>
                  <StyledTableCell colSpan={6} align="center">
                    <Typography style={{ opacity: 0.5, padding: "40px 0" }}>
                      {t("root:noRecordFound")}
                    </Typography>
                  </StyledTableCell>
                </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onChangePage={handleChangePage}
          rowsPerPage={rowsPerPage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 20, 100]}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                style: {
                  marginTop: "auto"
                },
              },
            },
          }}
        />
      </Box>
    </div>
  );
}

export default CreditNotes;